import { GETInvoiceTypeSuggestion, IDiscounts, IParamsData, IPostSupplierOrderData, IWorkorder, PUTDiscountResponse } from '.'
import { IAppointment } from '../workorder-appointment'
import { WorkorderLineStatus, WorkorderType } from '../enums'
import { IPaginatedRequest, IPaginatedRequestKeyloopWorkOrders, IPostKeyloopTransferBasket } from '../../request'
import { createAsyncApiAction } from '@cbgms/base/redux/create-async-action'
import { HttpMethod } from '@cbgms/api/http-method'

export const getWorkorderWithRelated = (workorderUUID: string) => ({
  method: 'GET',
  url: `/modules/workorders/${workorderUUID}/with-related`
})

export const getWorkorderWithRelatedPublic = (token: string) => ({
  method: 'GET',
  url: '/public/workorder/with-related',
  params: {
    token
  }
})

export const getAppointmentAvailability = (token: string) => ({
  method: 'GET',
  url: 'public/workorder-appointment/availability',
  params: {
    token
  }
})

export const getWorkorderAppointmentAvailability = (workorderUUID: string) => ({
  method: 'GET',
  url: `/modules/workorder-appointments/${workorderUUID}/availability`
})

export const createAppointmentProposal = (token: string, data: Partial<IAppointment>) => ({
  method: 'POST',
  url: 'public/workorder-appointment/proposal',
  params: {
    token
  },
  data
})

export const putWorkorderLineApprovalPublic = (token: string, lines: string[], status: WorkorderLineStatus) => ({
  method: 'PUT',
  url: '/public/workorder/line-approval',
  params: {
    token
  },
  data: {
    LineUUIDs: lines,
    Status: status
  }
})

export const updateWorkorder = (workorderUUID: string, data: Partial<IWorkorder>) => ({
  method: 'PUT',
  url: `/modules/workorders/${workorderUUID}`,
  data
})

export const restoreWorkorderFromInvoice = (invoiceUUID: string) => ({
  method: 'POST',
  url: `/modules/workorders/restore-from-invoice/${invoiceUUID}`
})

export const restoreWorkorderFromQuotation = (quotationUUID: string) => ({
  method: 'POST',
  url: `/modules/workorders/restore-from-quotation/${quotationUUID}`
})

export const getPreInvoiceCheck = (workorderUUID: string) => ({
  method: 'GET',
  url: `/modules/workorders/${workorderUUID}/pre-invoice-check`
})

export const searchQuickCreate = (query: string) => ({
  method: 'GET',
  url: '/modules/workorders/search-for-quick-create',
  params: {
    q: query
  }
})

export const getWorkorderSignals = (workorderUUID: string) => ({
  method: 'GET',
  url: `/modules/workorders/${workorderUUID}/signals`
})

export const getWorkorderImages = (workorderUUID: string) => ({
  method: 'GET',
  url: `/media/private/workorder-images/${workorderUUID}`
})

export const getVehicleProductHistory = (
  vehicleUUID: string,
  orderAccountUUID: string,
  excludeAvailability: boolean,
  searchTerm?: string,
  cursor?: string
) => ({
  method: 'GET',
  url: '/modules/products/history',
  params: {
    OrderAccountUUID: orderAccountUUID,
    VehicleUUID: vehicleUUID,
    ExcludeAvailability: excludeAvailability,
    ...(searchTerm ? { SearchTerm: searchTerm } : {}),
    ...(cursor ? { Cursor: cursor } : {})
  }
})

export const getWorkordersPortalDashboard = () => ({
  method: HttpMethod.GET,
  url: '/modules/workorders/portal-dashboard'
})

export const getWorkordersAssignedToEmployee = (params: IPaginatedRequest, shopID?: string) => ({
  method: 'GET',
  url: '/modules/workorders/assigned-to-me',
  headers:
    shopID &&
    new Headers({
      'X-Current-Shop-ID': shopID
    }),
  params
})

export const loadWorkordersByVehicle = (params: IPaginatedRequest) => ({
  method: 'GET',
  url: '/modules/workorders',
  params
})

export const loadWorkordersByAccount = (params: IPaginatedRequest) => ({
  method: 'GET',
  url: '/modules/workorders',
  params
})

export const createWorkorder = (data?: Partial<IWorkorder>) => ({
  method: 'POST',
  url: '/modules/workorders',
  data: data || {} // make sure to send an empty json, else the api will reject
})

export const deleteWorkorder = (UUID: string, dryRun?: boolean, reason?: string) => ({
  method: 'DELETE',
  url: `/modules/workorders/${UUID}`,
  params: {
    DryRun: dryRun,
    Reason: reason
  }
})

export const getWorkorders = (params: IPaginatedRequest) => ({
  method: 'GET',
  url: '/modules/workorders',
  params: {
    ...params,
    search: [params.search, `Workorder.WorkorderType|=|${WorkorderType.WorkShop}`].filter(Boolean).join(',')
  }
})

export const getWorkorderLines = (workorderUUID: string) => ({
  method: HttpMethod.GET,
  url: `/modules/workorders/${workorderUUID}/lines`
})

export const getProductGroups = () => ({
  method: 'GET',
  url: '/modules/product-groups'
})

export const getWholesalerIframeURL = (WorkorderUUID: string, WorkorderLineUUID: string, OrderAccountUUID: string) => ({
  method: 'GET',
  url: `/modules/workorders/${WorkorderUUID}/iframe`,
  params: {
    OrderAccountUUID,
    WorkorderLineUUID
  }
})

export const wholeSalerIframeCartToWorkorder = (
  WorkorderUUID: string,
  WorkorderLineUUID: string,
  OrderAccountUUID: string,
  SessionID: string
) => ({
  method: 'PUT',
  url: `/modules/workorders/${WorkorderUUID}/iframe`,
  data: {
    WorkorderUUID,
    WorkorderLineUUID,
    OrderAccountUUID,
    SessionID
  }
})

export const processExternalOrder = (WorkorderUUID: string, WorkorderLineUUID: string, OrderAccountUUID: string, SessionID: string) => ({
  method: 'PUT',
  url: `/modules/workorders/${WorkorderUUID}/process-external-order`,
  data: {
    WorkorderUUID,
    WorkorderLineUUID,
    OrderAccountUUID,
    SessionID
  }
})

export const postSupplierOrderParams = (workorderUUID: string, data: IParamsData) => ({
  method: 'POST',
  url: `/modules/workorders/${workorderUUID}/supplier-order/params`,
  data
})

export const putLinesAvailibilityRefresh = (workorderUUID: string, data: IParamsData) => ({
  method: 'PUT',
  url: `/modules/workorders/${workorderUUID}/refresh-availability`,
  data
})

export const postSupplierOrder = (workorderUUID: string, data: IPostSupplierOrderData) => ({
  method: 'POST',
  url: `/modules/workorders/${workorderUUID}/supplier-order`,
  data
})

export const postSupplierOrderQuickly = (workorderUUID: string) => ({
  method: 'POST',
  url: `/modules/workorders/${workorderUUID}/supplier-order-quickly`
})

export const setDiscounts = (workorderUUID: string, request: IDiscounts) => ({
  method: 'PUT',
  url: `/modules/workorders/${workorderUUID}/lines/discount`,
  data: request
})

export const putDiscounts = (workorderUUID: string, request: IDiscounts) =>
  createAsyncApiAction<PUTDiscountResponse>('gms', {
    method: 'PUT',
    url: `/modules/workorders/${workorderUUID}/lines/discount`,
    data: request
  })

export const notifyMyKar = (workorderUUID: string) => ({
  method: 'PUT',
  url: `/modules/workorders/${workorderUUID}/notify-mykar`
})

export const getInvoiceTypeSuggestion = (workorderUUID: string) =>
  createAsyncApiAction<GETInvoiceTypeSuggestion>('gms', {
    method: 'GET',
    url: `/modules/workorders/${workorderUUID}/invoice-type-suggestion`
  })

export const getWorkorderAction = (workorderUUID: string) => ({
  method: 'GET',
  url: `/modules/workorders/${workorderUUID}/actions`
})

export const deleteWorkorderAction = (UUID: string, actionUUID: string) => ({
  method: 'DELETE',
  url: `/modules/workorders/${UUID}/actions/${actionUUID}`
})

export const getKeyloopWorkOrders = (params: IPaginatedRequestKeyloopWorkOrders) => ({
  method: 'GET',
  url: '/modules/workorder-keyloop/repair-orders',
  params
})

export const getKeyloopWorkOrderRelatedJobs = (workorderUUID: string) => ({
  method: 'GET',
  url: `/modules/workorder-keyloop/repair-orders/${workorderUUID}`
})

export const postKeyloopJobTransfer = (data: IPostKeyloopTransferBasket) => ({
  method: 'POST',
  url: '/modules/workorder-keyloop/transfer-basket',
  data
})

export const updateWorkorderReference = (workOrderUUID: string, orderReference: string) => ({
  method: 'PUT',
  url: `/modules/workorders/${workOrderUUID}/update-orderreference`,
  data: {
    workorderReference: orderReference
  }
})
